import { useApolloClient } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Animated,
  Dimensions,
  Platform,
  TouchableWithoutFeedback,
} from 'react-native';

import TagsCollection from '@components/Chat/TagsCollection';
import { Box, Text } from '@components/Restyle';
import AcceptDeclineButtons from '@components/shared/Button/AcceptDeclineButtons';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { Tag } from '@components/shared/Tags/Tag';
import TooltipPopup, {
  getTooltipAnchorSpacing,
} from '@components/Tutorialization/TooltipPopup';
import { TooltipOnboardingWeb } from '@components/Web/TooltipOnboarding.web';
import {
  Message,
  useUpdateUserRequestStatusMutation,
  MessageProject,
} from '@graphql/generated';
import { useActiveTooltip } from '@hooks/useActiveTooltip';
import useChatInput from '@hooks/useChatInput';
import { useIsInvitedNewUser } from '@hooks/useInvitedUserOnboarding';
import useMe from '@hooks/useMe';
import { useOnboardingActive } from '@hooks/useOnboardingActive';
import { useAppNavigation } from '@navigation/useAppNavigation';
import theme from '@themes/theme';
import { AsyncStorage } from '@utils/storage';
import { generateTagCollections } from '@utils/tagCollectionService';
import { AssignTaskSourceCard } from '@utils/tasks/assignTaskSourceCard';
import {
  resolveIsInvitation,
  resolveMemberActivityAction,
} from '@utils/userRequestClassification';

const isWeb = Platform.OS === 'web';
const screenWidth = Dimensions.get('window').width;

// Module-level registry: userId -> Map<messageId, createdAt string>
// Used to determine which JoinRequest instance is the most recent (max createdAt)
// so only that one shows the "Put someone on it" onboarding tooltip on native.
const _joinRequestRegistry = new Map<string, Map<string, string>>();

function _registerJoinRequest(
  userId: string,
  messageId: string,
  createdAt: string
): void {
  if (!_joinRequestRegistry.has(userId)) {
    _joinRequestRegistry.set(userId, new Map());
  }
  _joinRequestRegistry.get(userId)!.set(messageId, createdAt);
}

function _unregisterJoinRequest(userId: string, messageId: string): void {
  _joinRequestRegistry.get(userId)?.delete(messageId);
}

// Tracks whether any JoinRequest instance is currently showing the
// "Put someone on it" tooltip — read by MyChatDetail to suppress the
// "Start a Conversation" tooltip when they would overlap.
let _assignTaskTooltipActiveCount = 0;
export const isAssignTaskTooltipActive = () =>
  _assignTaskTooltipActiveCount > 0;

function _isLatestJoinRequest(userId: string, messageId: string): boolean {
  const map = _joinRequestRegistry.get(userId);
  if (!map || map.size === 0) return false;
  let latestId = '';
  let latestAt = '';
  map.forEach((createdAt, id) => {
    if (createdAt > latestAt || (createdAt === latestAt && id > latestId)) {
      latestAt = createdAt;
      latestId = id;
    }
  });
  return latestId === messageId;
}

type JoinRequestProps = {
  message: Message;
  onLongPress?: () => void;
  showAcceptDecline?: boolean;
  filterVal?: string;
  doNotUseGlobalSearch?: boolean;
  isActivityItem?: boolean;
  isProject?: boolean;
};

export const JoinRequest: React.FC<JoinRequestProps> = ({
  message,
  onLongPress,
  showAcceptDecline,
  filterVal,
  doNotUseGlobalSearch,
  isActivityItem = false,
  isProject = false,
}) => {
  const { t } = useTranslation('format');
  const { attachments, userRequest, body } = message;
  const resolvedAuthorName = userRequest?.user?.name || 'User';
  // PublicTeam joined the Attachment union, so attachments[0] is no longer
  // guaranteed to be the MessageProject — match on __typename instead.
  const project = message?.attachments?.find(
    (attachment): attachment is MessageProject =>
      attachment?.__typename === 'MessageProject'
  )?.project;
  const { me } = useMe();
  const onboardingActive = useOnboardingActive();
  const invitedMember = useIsInvitedNewUser();
  const [updateUserRequestStatus] = useUpdateUserRequestStatusMutation({
    refetchQueries: ['listMessages', 'listProjects', 'getProject'],
    update: (cache, { data }) => {
      const updated = data?.updateUserRequestStatus;
      if (!updated?.id) return;
      cache.evict({
        id: cache.identify({ __typename: 'UserRequest', id: updated.id }),
      });
      cache.gc();
    },
  });
  const { navigateToProject } = useAppNavigation();
  const {
    setIsAssigningTask,
    setIsAssigningTaskStep2,
    setIsTagModalOpen,
    setIsCompletingTask,
    setAssignTaskSourceCard,
  } = useChatInput();

  const { isActive, show, hide } = useActiveTooltip(
    'join-request-assign-task',
    10
  );
  const navigation = useNavigation<any>();

  const client = useApolloClient();
  const tagsByAuthorCollections = generateTagCollections(attachments || []);
  const hasTags = tagsByAuthorCollections.some(
    (collections) => collections.length > 0
  );
  const isPending = userRequest?.status === 'pending';
  const isInvitation = resolveIsInvitation({
    userRequest,
    authorId: message.authorId,
    isSender: message.isSender,
    meId: me?.id,
  });
  const isInvitee = userRequest?.user?.id === me?.id;
  const isJoined =
    userRequest?.status === 'approved' || userRequest?.status === 'joined';
  const isJoinRequester = userRequest?.user?.id === me?.id;
  const invitedByFirstName =
    userRequest?.invitedBy?.name?.split(' ')[0] ||
    userRequest?.invitedBy?.name ||
    '';

  // TA-10740 / TA-10741: `canBeReviewed` is the backend's per-viewer answer to
  // "may you act on this request right now". It is what distinguishes a live
  // invitation (names the invitee) from an expired invite the user had to
  // re-request (names the inviter/creator) — the two are otherwise identical on
  // the wire. Undefined when the field is absent, so a missing value never
  // changes what the card renders.
  const memberActivityAction = resolveMemberActivityAction({
    status: userRequest?.status,
    isInvitee,
    isInvitation,
    canBeReviewed: userRequest?.canBeReviewed,
    showAcceptDecline,
    userRequestableType: userRequest?.userRequestableType,
  });
  // Someone else has to approve it — the request is waiting, not joinable.
  const isAwaitingApproval = memberActivityAction === 'request';
  // The viewer is the approver of this request.
  const isReviewer = memberActivityAction === 'review';

  const [showAssignTaskTooltip, setShowAssignTaskTooltip] = useState(false);
  const tooltipOpacity = useRef(new Animated.Value(1)).current;
  const fadeStarted = useRef(false);

  // Register this JoinRequest in the module-level registry so we can
  // determine which instance is the most recent (native only).
  useEffect(() => {
    if (!isWeb && me?.id && message.id && message.createdAt) {
      _registerJoinRequest(me.id, message.id, message.createdAt);
      return () => {
        _unregisterJoinRequest(me.id!, message.id);
      };
    }
  }, [me?.id, message.id, message.createdAt]);

  useEffect(() => {
    if (showAssignTaskTooltip) {
      _assignTaskTooltipActiveCount++;
      show();
    } else {
      hide();
    }
    return () => {
      if (showAssignTaskTooltip) _assignTaskTooltipActiveCount--;
    };
  }, [showAssignTaskTooltip, hide, show]);

  useEffect(() => {
    return () => {
      setShowAssignTaskTooltip(false);
      const tooltipKey = `assign_task_tooltip_shown_${me?.id}`;
      globalThis.sessionStorage?.removeItem(tooltipKey);
    };
  }, [me?.id]);

  const getInvitationDescription = () => {
    if (!isInvitation) return null;
    const userName = userRequest?.user?.name || t('models:chat.aUser');
    if (isPending) {
      // TA-10740: the invitee re-requested access, so this reads as a join
      // request on both sides — "Ask to join" for them, "Request to join" for
      // whoever has to approve it.
      if (isAwaitingApproval && isInvitee) {
        return t('models:chat.askToJoinThisProject');
      }
      if (isReviewer && !isInvitee) {
        return t('models:chat.requestToJoinThisProject');
      }
      if (isInvitee) {
        return t('models:chat.youveBeenInvitedToJoinThisProject');
      }
      return t('models:chat.userHasBeenInvitedToThisProject', {
        name: userName,
      });
    }
    if (isJoined) {
      if (isInvitee) {
        return t('models:chat.youveBeenAddedToThisProject');
      }
      return t('models:chat.userHasBeenAddedToThisProject', {
        name: userName,
      });
    }
    return null;
  };

  const getJoinRequestMessage = () => {
    if (isInvitation) return null;
    if (isPending) {
      return t('models:chat.requestToJoinThisProject');
    }
    if (
      userRequest?.status === 'approved' ||
      userRequest?.status === 'joined'
    ) {
      if (isJoinRequester) {
        return t('models:chat.youveBeenAddedToThisProject');
      }
      return t('models:chat.joinedProject', { name: resolvedAuthorName });
    }
    if (userRequest?.status === 'rejected') {
      if (isJoinRequester) {
        return t('models:chat.youHaveDeclinedThisProject');
      }
      return t('models:chat.declinedFromThisProject');
    }
    return null;
  };

  const description = getInvitationDescription();
  const joinRequestMessage = getJoinRequestMessage();

  useEffect(() => {
    // TA-10039: completed/legacy users (onboardingComplete !== false) never see
    // the "Put someone on it" assign-task onboarding tooltip.
    // TA-10920: "Put someone on it" is an owner step — invited members only
    // carry inviteMemberComplete=true as a skip marker, so never show it.
    if (
      !onboardingActive ||
      invitedMember !== false ||
      me?.onboardingState?.inviteMemberComplete === false ||
      project === undefined
    )
      return;

    // On native, only the most recent JoinRequest message shows the tooltip.
    if (!isWeb && me?.id && !_isLatestJoinRequest(me.id, message.id)) return;

    const storageKey = `invite_member_tooltip_shown_${me?.id}`;
    AsyncStorage?.getItem(storageKey).then((shown) => {
      if (shown === 'true') return;
      AsyncStorage?.setItem(storageKey, 'true');
      setTimeout(() => {
        setShowAssignTaskTooltip(true);
        tooltipOpacity.setValue(1);
        fadeStarted.current = false;
      }, 500);
    });
  }, [
    onboardingActive,
    invitedMember,
    me?.onboardingState?.inviteMemberComplete,
    project,
    me?.id,
    message.id,
  ]);

  const showInvitationJoinedCard =
    isInvitation && isJoined && (!isInvitee || !!invitedByFirstName);
  const showNonInvitationJoinedCard =
    !description && joinRequestMessage && isJoined;

  // "Assign a task" replaces this card with the TASK ASSIGNED card (TA-11038).
  const sourceCard: AssignTaskSourceCard | undefined =
    project && message.chatId
      ? { messageId: message.id, chatId: message.chatId, projectId: project.id }
      : undefined;

  const renderContent = () => {
    if (showInvitationJoinedCard) {
      const assignTaskButton = (
        <Button
          borderColor='black'
          backgroundColor='black'
          variant='create'
          isSmall={true}
          width={isWeb ? undefined : 140}
          height={isWeb ? 48 : 32}
          onPress={() => {
            if (isWeb && project) {
              globalThis.sessionStorage?.setItem(
                'assign_task_onboard_project_id',
                project.id
              );
            }
            if (isWeb && showAssignTaskTooltip) {
              const tooltipKey = `assign_task_tooltip_shown_${me?.id}`;
              globalThis.sessionStorage?.setItem(tooltipKey, 'true');
            }
            setShowAssignTaskTooltip(false);
            setIsTagModalOpen(false);
            setIsAssigningTaskStep2(false);
            setIsCompletingTask(false);
            if (!isWeb && message.chatId) {
              navigation.navigate('task-modal', {
                chatId: message.chatId,
                isFrom: 'Chat',
                projectId: showAssignTaskTooltip ? project?.id : undefined,
                showTaskStartWithTooltip: showAssignTaskTooltip,
                sourceCard,
              });
            } else {
              setAssignTaskSourceCard(sourceCard);
              setIsAssigningTask(true);
            }
          }}>
          <Text variant='labelSmall' lineHeight={isWeb ? 18 : 16} color='white'>
            {t('models:chat.assignATask')}
          </Text>
        </Button>
      );

      const nonInviteeButton = isWeb ? (
        <TooltipOnboardingWeb
          variant='left-center'
          tooltipStyle='success'
          title={t('models:nudge.putSomeoneOnIt.title')}
          description={t('models:nudge.putSomeoneOnIt.description')}
          open={isActive}
          forceShow={isActive}
          animatedOpacity={tooltipOpacity}
          offset={20}
          style={{ alignSelf: 'stretch' }}>
          {assignTaskButton}
        </TooltipOnboardingWeb>
      ) : (
        <Box
          width={screenWidth - theme.spacing.m * 6}
          style={{ overflow: 'visible' }}>
          {/* TA-10921 #21: anchored to the button's top edge so the arrow
              tip lands 4px directly above "Assign a task". */}
          <Box
            position='absolute'
            left={0}
            style={{
              bottom: '100%',
              paddingBottom: getTooltipAnchorSpacing(),
            }}>
            <TooltipPopup
              maxWidth={screenWidth - theme.spacing.m * 2}
              visible={showAssignTaskTooltip}
              title={t('models:nudge.putSomeoneOnIt.title')}
              description={t('models:nudge.putSomeoneOnIt.descriptionMobile')}
              pointerPosition='bottom-left'
              animate
            />
          </Box>
          {assignTaskButton}
        </Box>
      );

      return (
        <Box
          backgroundColor='white'
          borderBottomRightRadius='s'
          borderBottomLeftRadius='s'
          borderTopRightRadius='s'
          style={{ marginHorizontal: -theme.spacing.m }}>
          <Box
            backgroundColor='white'
            borderColor='grey02'
            borderWidth={1}
            borderRadius='xs'
            px='s'
            pt='xs'
            m='s'>
            <Box
              mb={isWeb ? 'm' : 's'}
              width={116}
              flexShrink={0}
              height={28}
              backgroundColor='greenSecondaryMild'
              borderRadius='m'
              alignItems='center'
              justifyContent='center'>
              <Text
                variant={isWeb ? 'metadataSemiBold' : 'metadataSecondaryNew'}
                color='textSecondary'>
                {t('models:chat.memberJoinedBadge')}
              </Text>
            </Box>
            <Text
              variant={isWeb ? 'labelEmphasized' : 'webSmall'}
              color='textPrimary'>
              {isInvitee
                ? t('models:chat.inviterAddedYouToThisProject', {
                    name: invitedByFirstName,
                  })
                : t('models:chat.joinedProject', { name: resolvedAuthorName })}
            </Text>
            <Text
              variant={isWeb ? 'webBodySecondary' : 'webBodyHintSubline'}
              color='textPrimary'
              mb={isWeb ? 'm' : 's'}>
              {isInvitee
                ? t('models:chat.youAreNowPartOf')
                : t('models:chat.memberJoinedIsNowPartOf', {
                    name:
                      userRequest?.user?.firstName ?? userRequest?.user?.name,
                  })}{' '}
              <Text
                mt='xxs'
                variant='webBody'
                fontSize={isWeb ? 14 : 12}
                color='greenSecondary'>
                {project?.name ?? 'project'}
              </Text>{' '}
              {t('models:chat.memberJoinedReadyToCollaborate')}
            </Text>
            <Box flexDirection='row' alignItems='center' paddingBottom='s'>
              {isInvitee ? (
                <Button
                  borderColor='black'
                  backgroundColor='black'
                  variant='create'
                  isSmall={true}
                  width={140}
                  height={isWeb ? 48 : 32}
                  onPress={() => {
                    if (project) {
                      client.cache.evict({
                        id: 'ROOT_QUERY',
                        fieldName: 'getProject',
                        args: { id: project.id },
                      });
                      client.cache.gc();
                      navigateToProject(project);
                    }
                  }}>
                  <Text
                    variant='labelSmall'
                    lineHeight={isWeb ? 18 : 16}
                    color='white'>
                    {t('models:chat.viewProject')}
                  </Text>
                </Button>
              ) : (
                nonInviteeButton
              )}
            </Box>
          </Box>
        </Box>
      );
    }
    if (showNonInvitationJoinedCard) {
      return (
        <Box
          backgroundColor='white'
          borderBottomRightRadius='s'
          borderBottomLeftRadius='s'
          borderTopRightRadius='s'
          style={{ marginHorizontal: -theme.spacing.m }}>
          <Box
            backgroundColor='white'
            borderColor='grey02'
            borderWidth={1}
            borderRadius='xs'
            px='s'
            pt='xs'
            m='s'>
            <Box
              mb='m'
              width={116}
              flexShrink={0}
              height={28}
              backgroundColor='greenSecondaryMild'
              borderRadius='m'
              alignItems='center'
              justifyContent='center'>
              <Text
                variant={isWeb ? 'metadataSemiBold' : 'metadataSecondaryNew'}
                color='textSecondary'>
                {t('models:chat.memberJoinedBadge')}
              </Text>
            </Box>

            <Text
              variant={isWeb ? 'labelEmphasized' : 'webSmall'}
              color='textPrimary'>
              {joinRequestMessage}
            </Text>
            <Text
              variant={isWeb ? 'webBodySecondary' : 'webBodyHintSubline'}
              color='textPrimary'
              mb='m'>
              {t('models:chat.memberJoinedIsNowPartOf', {
                name: resolvedAuthorName,
              })}{' '}
              <Text
                mt='xxs'
                variant='webBody'
                fontSize={isWeb ? 14 : 12}
                color='greenSecondary'>
                {project?.name ?? 'project'}
              </Text>{' '}
              {t('models:chat.memberJoinedReadyToCollaborate')}
            </Text>
            <Box flexDirection='row' alignItems='center' paddingBottom='s'>
              <Button
                borderColor='black'
                backgroundColor='black'
                variant='create'
                isSmall={true}
                width={isWeb ? undefined : 140}
                height={isWeb ? 48 : 32}
                onPress={() => {
                  if (isWeb && project) {
                    globalThis.sessionStorage?.setItem(
                      'assign_task_onboard_project_id',
                      project.id
                    );
                  }
                  if (isWeb && showAssignTaskTooltip) {
                    const tooltipKey = `assign_task_tooltip_shown_${me?.id}`;
                    globalThis.sessionStorage?.setItem(tooltipKey, 'true');
                  }
                  setShowAssignTaskTooltip(false);
                  setIsTagModalOpen(false);
                  setIsAssigningTaskStep2(false);
                  setIsCompletingTask(false);
                  if (!isWeb && message.chatId) {
                    navigation.navigate('task-modal', {
                      chatId: message.chatId,
                      isFrom: 'Chat',
                      projectId: showAssignTaskTooltip
                        ? project?.id
                        : undefined,
                      showTaskStartWithTooltip: showAssignTaskTooltip,
                      sourceCard,
                    });
                  } else {
                    setAssignTaskSourceCard(sourceCard);
                    setIsAssigningTask(true);
                  }
                }}>
                <Text
                  variant='labelSmall'
                  lineHeight={isWeb ? 18 : 16}
                  color='white'>
                  {t('models:chat.assignATask')}
                </Text>
              </Button>
            </Box>
          </Box>
        </Box>
      );
    }
    return (
      <Box
        borderColor='grey02'
        borderWidth={1}
        borderRadius='xs'
        {...(isWeb
          ? {
              marginVertical: isActivityItem ? 'none' : 's',
              backgroundColor: 'white',
            }
          : { marginBottom: 's', overflow: 'visible' as const })}>
        <Tag
          label={t('models:chat.memberActivity')}
          labelVariant='labelAssignedTask'
          variant='assignedTask'
          prefixMarginRight='xxs'
          prefix={<Icon name='Users' variant='s' color='pastelMagenta' />}
          padding='xs'
          borderTopLeftRadius='xxs'
          borderTopRightRadius='xxs'
          borderBottomLeftRadius='none'
          borderBottomRightRadius='none'
        />
        {description && (
          <Box marginHorizontal='s' marginTop='xs'>
            <Text variant='bodySecondary1'>{description}</Text>
          </Box>
        )}
        {!description && joinRequestMessage && (
          <Box marginHorizontal='s' marginTop='xs'>
            <Text variant='bodySecondary1'>{joinRequestMessage}</Text>
          </Box>
        )}
        {!description && !joinRequestMessage && !!body?.trim() && !hasTags && (
          <Box marginHorizontal='s' marginVertical='xs'>
            <Text variant='bodySecondary1'>{body}</Text>
          </Box>
        )}
        {tagsByAuthorCollections.map((tagCollections) => {
          return tagCollections.map((item) => {
            const { project, tasks, author } = item;

            return (
              <Box key={project?.id}>
                <TagsCollection
                  filterVal={filterVal ?? ''}
                  type='MemberActivity'
                  tagsCollection={{ project, tasks, author }}
                  showAuthor={false}
                  borderless={true}
                  isRow={isWeb}
                  isActivityItem={isActivityItem}
                  showProject={!isProject}
                  hasPaddingLeft
                  {...(!isWeb && { onLongPress })}
                  {...(isWeb && { doNotUseGlobalSearch })}
                />
              </Box>
            );
          });
        })}
        {isWeb && isInvitation && isInvitee && isJoined && (
          <Box
            flexDirection='row'
            alignItems='center'
            marginStart='xs'
            paddingBottom='s'>
            <Button
              backgroundColor='black'
              variant='create'
              isSmall={true}
              height={32}
              borderRadius='l'
              borderColor='black'
              onPress={() => {
                const project = tagsByAuthorCollections
                  .flat()
                  .find((c) => c.project)?.project;
                if (project) {
                  client.cache.evict({
                    id: 'ROOT_QUERY',
                    fieldName: 'getProject',
                    args: { id: project.id },
                  });
                  client.cache.gc();
                  navigateToProject(project);
                }
              }}>
              {t('models:projects.onboardingCard.goToProject')}
            </Button>
          </Box>
        )}
        {isInvitation && isInvitee && isPending && !isAwaitingApproval && (
          <Box
            flexDirection='row'
            alignItems='center'
            marginStart='xs'
            paddingBottom='s'>
            <Button
              backgroundColor='black'
              variant='create'
              isSmall={true}
              height={32}
              borderRadius='xs'
              borderColor='black'
              prefix={<Icon name='CheckCircle' variant='s' color='white' />}
              onPress={() =>
                updateUserRequestStatus({
                  variables: { id: userRequest?.id || '', status: 'joined' },
                })
              }>
              {t('models:chat.joinProject')}
            </Button>
          </Box>
        )}
        {isPending &&
          !isInvitee &&
          ((showAcceptDecline && !isInvitation) || isReviewer) && (
            <Box
              flexDirection='row'
              alignItems='center'
              paddingBottom='s'
              {...(isWeb
                ? { marginStart: 'xs', marginTop: 'xxs' }
                : { marginTop: 'xxs', marginHorizontal: 'xs' })}>
              <AcceptDeclineButtons
                requestId={userRequest?.id}
                suffix={true}
                borderRadius='xs'
              />
            </Box>
          )}
        {isWeb &&
          !isPending &&
          !isInvitation &&
          userRequest?.reviewedBy?.name && (
            <Box
              flex={1}
              flexDirection='row'
              marginStart='s'
              marginVertical='xxs'>
              <Text variant='bodySecondaryBold' marginRight='xxs'>
                {userRequest?.status === 'approved'
                  ? t('models:chat.addedStatus')
                  : t('models:chat.declinedStatus')}
              </Text>
              <Text variant='bodySecondary1'>
                {t('models:invitationPreview.task.byName', {
                  name: userRequest?.reviewedBy?.name,
                })}
              </Text>
            </Box>
          )}
      </Box>
    );
  };

  const content = renderContent();

  if (isWeb) {
    return content;
  }

  return (
    <TouchableWithoutFeedback onLongPress={onLongPress}>
      {content}
    </TouchableWithoutFeedback>
  );
};
